import { useEffect, useRef, useState } from 'react';
import { Alert, Stack } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { theme } from 'FrontRoyalMaterialUiForm';
import { FormProvider, useForm, yupResolver } from 'FrontRoyalReactHookForm';
import { useCurrentUser, useNavigationHelper } from 'FrontRoyalAngular';
import { hasAdminAccess } from 'Users';
import { useAppHeaderViewModel } from 'NavigationHelpers';
import { playlistEditorPath } from 'Playlists';
import { showToast } from 'Toast';
import {
    ConfirmDeleteItemDialog,
    DuplicateContentItemDialog,
    ERROR_TOAST_MS,
    StaleVersionDialog,
    entityMetadataApi,
    entityMetadataFromForm,
    messageFrom,
    useEditorRecord,
    type DuplicateOptions,
    type SavePublishState,
} from 'ContentItemEditor';
import PlaylistEditorForm from './PlaylistEditorForm';
import { playlistEditorApi } from './playlistEditorApi';
import { toForm } from './formFieldConversions';
import { usePlaylistSave } from './usePlaylistSave';
import { validationSchema } from './validationSchema';
import { type PlaylistFormValues } from './PlaylistEditor.types';

const PLAYLISTS_LIST_ROUTE = '/editor?section=playlists';

// oxlint-disable-next-line max-lines-per-function -- the page holds the data and every action
export default function PlaylistEditor({ playlistId }: { playlistId: string }) {
    const currentUser = useCurrentUser();
    const { loadRoute } = useNavigationHelper();
    const appHeaderViewModel = useAppHeaderViewModel();

    // The grey body background separates the cards from the page behind them.
    useEffect(() => {
        appHeaderViewModel.setBodyBackground();
        appHeaderViewModel.setTitleHTML('EDIT<br>PLAYLIST');
        window.scrollTo(0, 0);
    }, [appHeaderViewModel]);

    const { data: loaded, error } = playlistEditorApi.useGetPlaylistQuery(playlistId);
    const { data: linkablePlaylists, isLoading: playlistsLoading } =
        playlistEditorApi.useGetPlaylistsForEditorListQuery();

    const [updateEntityMetadata] = entityMetadataApi.useUpdateEntityMetadataMutation();
    const [uploadEntityMetadataImages] = entityMetadataApi.useUploadEntityMetadataImagesMutation();
    const [uploadPlaylistImages] = playlistEditorApi.useUploadPlaylistImagesMutation();
    const [destroyPlaylist] = playlistEditorApi.useDestroyPlaylistMutation();
    const [duplicatePlaylist] = playlistEditorApi.useDuplicatePlaylistMutation();

    const { record, recordDirty, applyEdit, applySaved } = useEditorRecord(loaded?.record);

    const [formReady, setFormReady] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [savingMetadata, setSavingMetadata] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [duplicateOpen, setDuplicateOpen] = useState(false);
    const [hideIsTranslation, setHideIsTranslation] = useState(false);
    const [duplicating, setDuplicating] = useState(false);
    const [publishState, setPublishState] = useState<SavePublishState>({
        shouldPublish: false,
        shouldUnpublish: false,
    });

    const formFunctions = useForm<PlaylistFormValues>({
        mode: 'all',
        resolver: yupResolver(validationSchema),
        reValidateMode: 'onChange',
    });

    const { reset, getValues } = formFunctions;

    // The effect below reads the record through a ref, so a patch to the cached record does not
    // re-run it.
    const loadedRef = useRef(record);
    loadedRef.current = record;
    const loadedPlaylistId = loaded?.record?.id;

    // The form does not render until this has run, or every input flips from uncontrolled to
    // controlled and the admin sees a blank form for a frame.
    useEffect(() => {
        if (!loadedPlaylistId) return;

        reset(toForm(loadedRef.current!));
        setFormReady(true);
    }, [loadedPlaylistId, reset]);

    const { save, saving, justSaved, staleVersion, dismissStaleVersion } = usePlaylistSave({
        record: record!,
        onSaved: saved => {
            applySaved(saved);
            reset(toForm(saved));

            // Clear them, or the next save republishes unasked.
            setPublishState({ shouldPublish: false, shouldUnpublish: false });
        },
    });

    if (!record || !formReady) {
        if (error) {
            const status = (error as { status?: number }).status;

            return (
                <Alert severity="error">
                    {status === 404
                        ? 'That playlist could not be found.'
                        : status === 401
                          ? 'You do not have permission to edit this playlist.'
                          : 'The playlist could not be loaded. Reload the page to try again.'}
                </Alert>
            );
        }

        return <FrontRoyalSpinner />;
    }

    async function uploadImage(files: File[], target: 'playlist' | 'seo') {
        setUploadingImage(true);

        try {
            const assets =
                target === 'playlist'
                    ? await uploadPlaylistImages({ playlistId, files }).unwrap()
                    : await uploadEntityMetadataImages({
                          entityMetadataId: record!.entity_metadata!.id,
                          files,
                      }).unwrap();

            const image = assets[0];
            if (!image) return;

            if (target === 'playlist') {
                applyEdit({ image });
            } else {
                applyEdit({ entity_metadata: { ...record!.entity_metadata!, image } });
            }
        } catch (uploadError) {
            showToast({
                message: 'The image was not uploaded',
                description: messageFrom(uploadError, 'Something went wrong'),
                type: 'danger',
                timeout: ERROR_TOAST_MS,
            });
        } finally {
            setUploadingImage(false);
        }
    }

    async function saveMetadata() {
        const entityMetadata = entityMetadataFromForm(getValues(), record!);
        if (!entityMetadata) return;

        setSavingMetadata(true);

        try {
            const saved = await updateEntityMetadata({ record: entityMetadata }).unwrap();

            applyEdit({ entity_metadata: saved }, { dirties: false });
            showToast({ message: 'Metadata has been updated!', type: 'success' });
        } catch (metadataError) {
            showToast({
                message: 'The metadata was not saved',
                description: messageFrom(metadataError, 'Something went wrong'),
                type: 'danger',
                timeout: ERROR_TOAST_MS,
            });
        } finally {
            setSavingMetadata(false);
        }
    }

    async function confirmDelete() {
        setDeleting(true);

        try {
            await destroyPlaylist(playlistId).unwrap();
            setConfirmDeleteOpen(false);
            loadRoute(PLAYLISTS_LIST_ROUTE);
        } catch (deleteError) {
            showToast({
                message: 'The playlist was not deleted',
                description: messageFrom(deleteError, 'A published playlist must be unpublished first.'),
                type: 'danger',
                timeout: ERROR_TOAST_MS,
            });
        } finally {
            setDeleting(false);
        }
    }

    async function confirmDuplicate({ title, locale, isTranslation }: DuplicateOptions) {
        setDuplicating(true);

        try {
            const duplicated = await duplicatePlaylist({
                title,
                locale: isTranslation ? locale : record!.locale,
                duplicateFromId: record!.id,
                duplicateFromUpdatedAt: record!.updated_at,
                inSameLocalePack: isTranslation,
            }).unwrap();

            setDuplicateOpen(false);
            loadRoute(playlistEditorPath({ playlistId: duplicated.record.id }));
        } catch (duplicateError) {
            showToast({
                message: 'The playlist was not duplicated',
                description: messageFrom(duplicateError, 'Something went wrong'),
                type: 'danger',
                timeout: ERROR_TOAST_MS,
            });
        } finally {
            setDuplicating(false);
        }
    }

    const titlesByLocale = Object.fromEntries(
        (record.locale_pack?.content_items || []).map(contentItem => [contentItem.locale, contentItem.title]),
    );

    return (
        <ThemeProvider theme={theme}>
            {/*
                The widths the Bootstrap `.container` gave this page before the conversion. The
                playlist list page and the lesson editor are still Angular inside that container, so
                a full-width editor would jump against them.
            */}
            <Stack
                sx={{
                    width: '100%',
                    maxWidth: { sm: 750, md: 970, lg: 1170 },
                    marginInline: 'auto',
                    paddingInline: 2,
                    paddingBlock: 3,
                }}
            >
                <FormProvider {...formFunctions}>
                    <PlaylistEditorForm
                        record={record}
                        recordDirty={recordDirty}
                        availableContentFamilyIdentifiers={loaded?.availableContentFamilyIdentifiers || []}
                        linkablePlaylists={linkablePlaylists || []}
                        playlistsLoading={playlistsLoading}
                        canPublish={hasAdminAccess(currentUser)}
                        saving={saving}
                        justSaved={justSaved}
                        deleting={deleting}
                        uploadingImage={uploadingImage}
                        savingMetadata={savingMetadata}
                        publishState={publishState}
                        onPublishStateChange={setPublishState}
                        onRecordEdit={applyEdit}
                        onSave={() =>
                            save(getValues(), {
                                should_publish: publishState.shouldPublish,
                                should_unpublish: publishState.shouldUnpublish,
                            })
                        }
                        onDelete={() => setConfirmDeleteOpen(true)}
                        onDuplicate={({ hideIsTranslation: hide }) => {
                            setHideIsTranslation(hide);
                            setDuplicateOpen(true);
                        }}
                        onSaveMetadata={saveMetadata}
                        onUploadImage={files => uploadImage(files, 'playlist')}
                        onRemoveImage={() => applyEdit({ image: null })}
                        onUploadSeoImage={files => uploadImage(files, 'seo')}
                        onGoBack={() => loadRoute(PLAYLISTS_LIST_ROUTE)}
                    />
                </FormProvider>

                <ConfirmDeleteItemDialog
                    open={confirmDeleteOpen}
                    itemLabel="playlist"
                    itemTitle={record.title}
                    deleting={deleting}
                    onCancel={() => setConfirmDeleteOpen(false)}
                    onConfirm={confirmDelete}
                />

                <DuplicateContentItemDialog
                    // Remounted on each open, so the default title follows a rename and the
                    // locale picker is re-derived.
                    key={`${duplicateOpen}-${hideIsTranslation}`}
                    open={duplicateOpen}
                    itemTitle={record.title}
                    itemLocale={record.locale}
                    titlesByLocale={titlesByLocale}
                    saving={duplicating}
                    hideIsTranslation={hideIsTranslation}
                    onCancel={() => setDuplicateOpen(false)}
                    onConfirm={confirmDuplicate}
                />

                <StaleVersionDialog
                    message={staleVersion}
                    itemLabel="playlist"
                    onDismiss={dismissStaleVersion}
                    onReload={() => {
                        dismissStaleVersion();
                        window.location.reload();
                    }}
                />
            </Stack>
        </ThemeProvider>
    );
}
